import React, { useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { login, register } from '../../api';
import { AuthContext } from '../../App';
import { t, useLang } from '../../i18n';
import { Icon, LangSwitcher, LogoTile, useToast } from './Kit';
import '../../styles/vod.css';

export default function AuthScreen({ mode: initialMode = 'signup' }) {
  useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useContext(AuthContext);
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [agree, setAgree] = useState(false);
  const [errs, setErrs] = useState({});
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, toastNode] = useToast();
  const up = mode === 'signup';
  const next = new URLSearchParams(location.search).get('next') || '';

  const switchMode = (m) => { setMode(m); setErrs({}); setMsg(''); };

  async function submit(e) {
    e.preventDefault();
    const bad = {};
    const missing = [];
    if (up && !name.trim()) { bad.name = 1; missing.push(t('f_name')); }
    if (!/^\S+@\S+\.\S+$/.test(email)) { bad.email = 1; missing.push(t('f_email')); }
    if (password.length < (up ? 6 : 1)) { bad.password = 1; missing.push(t('f_password')); }
    setErrs(bad);
    if (missing.length) { setMsg(t('err_fill', { fields: missing.join(', ') })); return; }
    if (up && !agree) { setErrs({ terms: 1 }); setMsg(''); toast(t('accept_terms')); return; }
    setMsg(''); setBusy(true);
    try {
      const data = up ? await register(name.trim(), email.trim().toLowerCase(), password) : await login(email.trim().toLowerCase(), password);
      setUser(data.user);
      navigate(next || (data.user && data.user.is_admin && !up ? '/admin' : '/excursions'), { replace: true });
    } catch (err) {
      setMsg(err.message);
    }
    setBusy(false);
  }

  return (
    <main className="vod vod-screen">
      <div className="vod-hero veil" style={{ height: '100%', backgroundImage: 'url(/auth-hero.jpg)', backgroundSize: '104% auto', backgroundPosition: 'center -30px' }} />
      <div className="vod-top" style={{ minHeight: 290 }}>
        <div style={{ paddingInlineStart: 20 }}>
          <LogoTile size="lg" />
          <div style={{ width: 92, height: 1.5, margin: '16px 0 14px', background: 'linear-gradient(90deg, #D9B56A, rgba(217,181,106,0))' }} />
          <p style={{ margin: 0, color: '#fff', fontWeight: 300, fontSize: 14, lineHeight: '22px', letterSpacing: '.32em', textTransform: 'uppercase', whiteSpace: 'pre-line', textShadow: '0 1px 8px rgba(0,0,0,.35)' }}>{t('tagline')}</p>
        </div>
        <LangSwitcher />
      </div>

      <form className="vod-card" onSubmit={submit} noValidate>
        <div className="vod-accent" style={{ width: 32 }} />
        <h1 className="vod-h1" style={{ fontSize: 28, lineHeight: '34px' }}>{up ? t('create_account') : t('welcome_back')}</h1>
        <p style={{ margin: '8px 0 18px', fontSize: 15, lineHeight: '22px', letterSpacing: '.03em', color: 'var(--vod-text2)', whiteSpace: 'pre-line' }}>{up ? t('create_sub') : t('welcome_sub')}</p>

        {up && (
          <label className={`vod-field ${errs.name ? 'err' : ''}`}>
            {Icon.user()}
            <input type="text" placeholder={t('your_name')} autoComplete="name" value={name} onChange={e => setName(e.target.value)} />
          </label>
        )}
        <label className={`vod-field ${errs.email ? 'err' : ''}`}>
          {Icon.mail()}
          <input type="email" inputMode="email" placeholder={t('email')} autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} dir="ltr" />
        </label>
        <label className={`vod-field ${errs.password ? 'err' : ''}`}>
          {Icon.lock()}
          <input type={showPw ? 'text' : 'password'} placeholder={t('password')} autoComplete={up ? 'new-password' : 'current-password'} value={password} onChange={e => setPassword(e.target.value)} />
          <button type="button" onClick={() => setShowPw(s => !s)} style={{ border: 0, background: 'none', padding: 4, cursor: 'pointer', display: 'flex' }} aria-label="password">
            {showPw ? Icon.eye() : Icon.eyeOff()}
          </button>
        </label>
        {!up && (
          <button type="button" onClick={() => navigate('/reset-password')} style={{ display: 'block', marginInlineStart: 'auto', margin: '-2px 2px 8px', border: 0, background: 'none', color: 'var(--vod-gold)', fontWeight: 600, fontSize: 14, cursor: 'pointer', font: 'inherit' }}>{t('forgot')}</button>
        )}
        {msg && <p className="vod-err">{msg}</p>}

        {up && (
          <label style={{ display: 'flex', gap: 12, alignItems: 'flex-start', margin: '8px 0 18px', cursor: 'pointer' }}>
            <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }} />
            <span style={{ flex: 'none', width: 22, height: 22, borderRadius: 6, border: `1.3px solid ${errs.terms ? '#D9534F' : '#B9B3A8'}`, background: agree ? 'var(--vod-navy)' : '#fff', display: 'grid', placeItems: 'center' }}>
              {agree && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5 9-10" /></svg>}
            </span>
            <span style={{ fontSize: 13.5, lineHeight: '20px', color: 'var(--vod-text2)' }}>
              {t('agree_1')}<a href="/privacy" target="_blank" rel="noreferrer" style={{ color: 'var(--vod-gold)', fontWeight: 500 }}>{t('terms')}</a>{t('agree_and')}<a href="/privacy" target="_blank" rel="noreferrer" style={{ color: 'var(--vod-gold)', fontWeight: 500 }}>{t('privacy')}</a>
            </span>
          </label>
        )}

        <button className="vod-btn" type="submit" disabled={busy} style={{ marginTop: up ? 0 : 8 }}>
          {busy ? t('loading') : (up ? t('get_started') : t('sign_in'))}
          {!busy && Icon.arrow()}
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '0 36px', margin: '18px 0 14px', color: '#8A8E9C', fontSize: 14 }}>
          <span style={{ flex: 1, height: 1, background: 'var(--vod-line)' }} />{t('or')}<span style={{ flex: 1, height: 1, background: 'var(--vod-line)' }} />
        </div>
        <p style={{ margin: 0, textAlign: 'center', fontSize: 14.5, color: 'var(--vod-text2)' }}>
          {up ? t('have_account') : t('new_here')}{' '}
          <button type="button" onClick={() => switchMode(up ? 'signin' : 'signup')} style={{ border: 0, background: 'none', padding: 0, color: 'var(--vod-gold)', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2, cursor: 'pointer', font: 'inherit' }}>
            {up ? t('sign_in') : t('create_account_link')}
          </button>
        </p>
      </form>
      {toastNode}
    </main>
  );
}
